import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { apiClient } from '@/lib/apiClient';

interface AccountRow {
  id: string;
  name: string;
  industry: string | null;
  contractRenewalDate: string | null;
  healthScore: number | null;
}

interface OpportunityRow {
  id: string;
  name: string;
  estimatedDealValue: string;
  status: string;
  forecastClassification: string;
}

export function AmWorkspacePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [expansion, setExpansion] = useState<OpportunityRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [acctRes, oppRes] = await Promise.all([
        apiClient.get<AccountRow[]>('/accounts', { params: { limit: 30 } }),
        apiClient.get<{ items: OpportunityRow[] }>('/opportunities', {
          params: { status: 'OPEN', forecastClassification: 'UPSIDE', limit: 10 },
        }),
      ]);
      setAccounts(acctRes.data);
      const oppData = oppRes.data;
      setExpansion(Array.isArray(oppData) ? oppData : (oppData as { items: OpportunityRow[] }).items ?? []);
    } catch {
      setAccounts([]);
      setExpansion([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Renewals coming up: accounts with renewal date in next 90 days
  const today = new Date();
  const cutoff = new Date(today.getTime() + 90 * 86400000);
  const renewals = accounts
    .filter((a) => {
      if (!a.contractRenewalDate) return false;
      const d = new Date(a.contractRenewalDate);
      return d >= today && d <= cutoff;
    })
    .sort((a, b) => new Date(a.contractRenewalDate!).getTime() - new Date(b.contractRenewalDate!).getTime());

  function daysUntil(date: string) {
    const diff = new Date(date).getTime() - today.getTime();
    return Math.max(0, Math.ceil(diff / 86400000));
  }

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <Breadcrumbs crumbs={[{ label: t('workspace.am_title') }]} />
      <h1 className="text-2xl font-semibold mt-2">{t('workspace.am_title')}</h1>
      <p className="text-muted-foreground text-sm mt-1">{t('workspace.am_subtitle')}</p>

      {loading ? (
        <p className="text-muted-foreground mt-6 text-sm">{t('common.loading')}</p>
      ) : (
        <div className="mt-6 space-y-6">
          {/* Renewals */}
          <div className="border rounded-xl p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              {t('workspace.renewal_countdown')}
            </h2>
            {renewals.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('workspace.empty_accounts')}</p>
            ) : (
              <div className="space-y-2">
                {renewals.map((a) => (
                  <button
                    key={a.id}
                    onClick={() => navigate(`/accounts/${a.id}`)}
                    className="w-full text-left flex items-center justify-between px-3 py-2 rounded-lg hover:bg-muted transition-colors text-sm"
                  >
                    <div>
                      <span className="font-medium">{a.name}</span>
                      {a.industry && <span className="text-xs text-muted-foreground ml-2">{a.industry}</span>}
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      daysUntil(a.contractRenewalDate!) <= 30
                        ? 'bg-red-900/40 text-red-200'
                        : daysUntil(a.contractRenewalDate!) <= 60
                        ? 'bg-amber-900/40 text-amber-200'
                        : 'bg-blue-900/40 text-blue-200'
                    }`}>
                      {daysUntil(a.contractRenewalDate!)}d
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* My accounts */}
          <div className="border rounded-xl p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              {t('workspace.accounts_section')}
            </h2>
            {accounts.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('workspace.empty_accounts')}</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {accounts.slice(0, 12).map((a) => (
                  <button
                    key={a.id}
                    onClick={() => navigate(`/accounts/${a.id}`)}
                    className="text-left flex items-center justify-between px-3 py-2 rounded-lg hover:bg-muted transition-colors text-sm border"
                  >
                    <div>
                      <span className="font-medium">{a.name}</span>
                      {a.industry && <span className="text-xs text-muted-foreground ml-2">{a.industry}</span>}
                    </div>
                    {a.healthScore != null && (
                      <span className={`text-xs font-mono ${a.healthScore >= 70 ? 'text-emerald-600' : a.healthScore >= 40 ? 'text-amber-600' : 'text-red-600'}`}>
                        {a.healthScore}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Expansion opportunities */}
          <div className="border rounded-xl p-4">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground mb-3">
              {t('workspace.expansion_section')}
            </h2>
            {expansion.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t('workspace.empty_expansion')}</p>
            ) : (
              <div className="space-y-2">
                {expansion.map((opp) => (
                  <button
                    key={opp.id}
                    onClick={() => navigate(`/opportunities/${opp.id}`)}
                    className="w-full text-left flex items-center justify-between px-3 py-2 rounded-lg hover:bg-muted transition-colors text-sm"
                  >
                    <span className="font-medium">{opp.name}</span>
                    <span className="text-muted-foreground text-xs">
                      {Number(opp.estimatedDealValue).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
